import React, { useEffect, useState } from 'react';
import { userDetailsWrapper } from '../../../../shared/lib/theme/styleConstants';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Paper, Radio, Stack, Switch, TextField, Typography } from '@mui/material';
import { useAppContext } from '../../app/contexts/AppContext';
import { Alert } from '../../../../shared/lib/common/Alert';
import { useFetchDefaultMessageLazyQuery, useGetUserLazyQuery, useUserRingMyPhoneSettingsMutation } from '../../generated';
import { BUTTON_TEXT_SAVE, GRAPHQL_QUERY_POLICY, LABEL_CUSTOM_MESSAGE, LABEL_DEFAULT_MESSAGE, LABEL_ENABLE_LOST_PHONE_RINGER, LABEL_RING_MY_PHONE_SETTINGS, LABEL_SHOW_855_LOGO, MSG_MESSAGE_TOO_SHORT, MSG_TEXT_LIMIT_EXCEEDED, PLACEHOLDER_CUSTOM_MESSAGE } from '../../../../shared/lib/constants';
import { withTrademark } from '../../../../shared/lib/common/Tokenizer';
import CardLockComponents from './CardLockComponents';

const RingMyPhoneMessageSettings = () => {
  const limit = 500;
  const { currentUser, setCurrentUser, selectedPlan } = useAppContext();
  const [selected, setSelected] = useState<boolean>(true);
  const [message, setMessage] = useState<string>('');
  const [show, setShow] = useState<boolean>(false);
  const [userDefaultMessage, setUserDefaultMessage] = useState<string>();

  const [enablePhone, setEnablePhone] = useState<boolean>(false);

  const { enablePhoneRinger, message: userMessage, defaultMessage, showLogo } = currentUser || {};

  const [fetchUser, { loading: userLoading }] = useGetUserLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onCompleted(data) {
      if (data) {
        const { me } = data;

        if (me) {
          setCurrentUser(me);
        }
      }
    }
  });

  const [fetchDefaultMessage, { loading: fetchLoading }] = useFetchDefaultMessageLazyQuery({
    onCompleted: (data) => {
      const { fetchDefaultMessage } = data;
      if (fetchDefaultMessage) {
        setUserDefaultMessage(fetchDefaultMessage);
      }
    },
    onError: ({ message }) => {
      Alert.error(message);
      setUserDefaultMessage('');
    }
  });

  const [saveSetting, { loading: saveLoading }] = useUserRingMyPhoneSettingsMutation({
    onCompleted: async (data) => {
      const { userRingMyPhoneSettings } = data;
      await fetchUser();
      Alert.success(userRingMyPhoneSettings);
    },
    onError: ({ message }) => {
      Alert.error(message);
    }
  });

  const setValues = () => {
    setEnablePhone(enablePhoneRinger ?? false);
    setMessage(userMessage ?? '');
    setSelected(defaultMessage ?? true);
    setShow(showLogo ?? true);
  };

  const onSave = async () => {
    if (!selected && message?.length >= limit + 1) {
      Alert.error(MSG_TEXT_LIMIT_EXCEEDED);
      return;
    }

    if (!selected && message?.length < 20) {
      Alert.error(MSG_MESSAGE_TOO_SHORT);
      return;
    }

    await saveSetting({
      variables: {
        UserRingMyPhoneInput: {
          defaultMessage: selected,
          enablePhoneRinger: enablePhone,
          ...(!selected && { message: message?.trim() || '' }),
          showLogo: show ?? true
        }
      }
    });
  };

  useEffect(() => {
    fetchDefaultMessage();
  }, []);

  useEffect(() => {
    setValues();
  }, [currentUser]);

  const loading = saveLoading || userLoading || fetchLoading;

  return (
    <Box sx={userDetailsWrapper} marginBottom="24px">
      {loading ? (
        <Box textAlign="center" mt={2}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          {selectedPlan?.nickname === 'Free plan' ? (
            <CardLockComponents />
          ) : (
            <>
              <Typography variant="h6" sx={{ color: 'blackOne', fontWeight: '700', pb: '24px' }}>
                {withTrademark(LABEL_RING_MY_PHONE_SETTINGS)}
              </Typography>

              <Box mb={2}>
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Switch
                      checked={enablePhone}
                      value={enablePhone}
                      onChange={(_, value) => {
                        setEnablePhone(value);
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: 'blackOne', fontWeight: '600' }} variant="body2">
                      {LABEL_ENABLE_LOST_PHONE_RINGER}
                    </Typography>
                  }
                  labelPlacement="start"
                />
              </Box>

              <Box>
                <FormControlLabel
                  value="default"
                  control={<Radio checked={selected} onChange={() => setSelected(true)} color="primary" />}
                  label={
                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                      {LABEL_DEFAULT_MESSAGE}
                    </Typography>
                  }
                />

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderColor: selected ? 'primary.main' : 'grey.300',
                    borderWidth: selected ? 2 : 1,
                    borderRadius: 2,
                    mb: 2,
                    mt: 1
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Box>
                      <Typography variant="body2" color="text.primary">
                        {userDefaultMessage}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Box>
              <Box>
                <Box display="flex" justifyContent="space-between">
                  <FormControlLabel
                    value="default"
                    control={<Radio checked={!selected} onChange={() => setSelected(false)} color="primary" />}
                    label={
                      <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                        Custom Message
                      </Typography>
                    }
                  />

                  <FormControlLabel
                    value="default"
                    control={
                      <Checkbox
                        checked={show}
                        disabled={selected}
                        onChange={() =>
                          setShow((prev) => {
                            return !prev;
                          })
                        }
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                        {LABEL_SHOW_855_LOGO}
                      </Typography>
                    }
                  />
                </Box>
                <Box mt={1}>
                  <TextField
                    label={LABEL_CUSTOM_MESSAGE}
                    placeholder={PLACEHOLDER_CUSTOM_MESSAGE}
                    multiline
                    fullWidth
                    value={message}
                    onChange={(event) => {
                      if (event?.target?.value?.trim().length <= limit + 1) {
                        setMessage(event.target.value ?? '');
                      }
                    }}
                    disabled={selected}
                    minRows={4}
                    maxRows={4}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: 'auto !important', // override theme height
                        alignItems: 'flex-start'
                      }
                    }}
                  />

                  <Typography
                    variant="subtitle2"
                    color={message?.length > limit || (!selected && message?.length < 20) ? 'error' : 'text.secondary'}
                    sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
                  >
                    {message?.length}/{limit}
                    {message?.length > limit && ` — ${MSG_TEXT_LIMIT_EXCEEDED}`}
                    {!selected && message?.length < 20 && `  —  ${MSG_MESSAGE_TOO_SHORT}`}
                  </Typography>
                </Box>
              </Box>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button variant="contained" onClick={onSave}>
                  {BUTTON_TEXT_SAVE}
                </Button>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default RingMyPhoneMessageSettings;
